import {
  type ButtonProps,
  createTheme,
  type CSSVariablesResolver,
  type MantineColorsTuple,
  type MantineTheme,
  rem,
} from '@mantine/core'

// ─── Brand blues ────────────────────────────────────────────────────────────
// Index 7 = Main Blue (#003591) — used as primaryShade.light
// Index 3 = Blue Highlight 2 (#DBEBF2) — input backgrounds
// Index 2 = Blue Highlight 3 (#E7F5F8) — active-tab background
const brand: MantineColorsTuple = [
  '#FFFFFF', // 0
  '#F7F7F7', // 1
  '#E7F5F8', // 2  Blue Highlight 3 ★
  '#DBEBF2', // 3  Blue Highlight 2 ★
  '#A4E3F7', // 4
  '#11A4D4', // 5
  '#0A63B4', // 6
  '#003591', // 7  Main Blue ★
  '#04215C', // 8
  '#010E2C', // 9
]

// ─── Semantic text grays ─────────────────────────────────────────────────────
// 0 = Main Text (#333333), 2 = Text 2, 4 = Text 3, 6 = Text 4 (borders only)
const text: MantineColorsTuple = [
  '#333333', // 0  Main Text
  '#444444', // 1
  '#555555', // 2  Text 2
  '#666666', // 3
  '#777777', // 4  Text 3
  '#888888', // 5
  '#999999', // 6  Text 4 — use for borders only
  '#AAAAAA', // 7
  '#BBBBBB', // 8
  '#CCCCCC', // 9
]

// ─── Structural grays ────────────────────────────────────────────────────────
// Guide tokens: D5 (#D5D5D5), D9 (#D9D9D9), F5 (#F5F5F5)
const mgray: MantineColorsTuple = [
  '#D5D5D5', // 0
  '#D9D9D9', // 1
  '#E0E0E0', // 2
  '#E8E8E8', // 3
  '#EEEEEE', // 4
  '#F5F5F5', // 5  Gray F5 ★  — section backgrounds
  '#F7F7F7', // 6
  '#F9F9F9', // 7
  '#FAFAFA', // 8
  '#FFFFFF', // 9
]

// ─── Semantic status colors ───────────────────────────────────────────────────
const success: MantineColorsTuple = [
  '#E8F5E9',
  '#C8E6C9',
  '#A5D6A7',
  '#81C784',
  '#4CAF50',
  '#2E7D32',
  '#0B8710', // 6  Main ★
  '#076108',
  '#044505',
  '#022902',
]

const warning: MantineColorsTuple = [
  '#FFF8E1',
  '#FFE082',
  '#FFCA28',
  '#FFB300',
  '#D4870D',
  '#C07B0A',
  '#A06A0B', // 6  Main ★
  '#7A5009',
  '#563807',
  '#352205',
]

const danger: MantineColorsTuple = [
  '#FFEBEE',
  '#FFCDD2',
  '#EF9A9A',
  '#E57373',
  '#EF5350',
  '#E53935',
  '#C1121C', // 6  Main ★
  '#9B0E16',
  '#750A10',
  '#4E070B',
]

// Which themed fill a Button uses, if any. Only filled buttons in the primary (no color /
// brand) or success colours are themed; everything else keeps Mantine's own colours.
function getFilledButtonToken({ variant, color }: ButtonProps): 'primary' | 'success' | undefined {
  if ((variant ?? 'filled') !== 'filled') return undefined
  if (!color || color === 'brand') return 'primary'
  if (color.startsWith('success')) return 'success'
  return undefined
}

export const theme = createTheme({
  colors: {
    brand,
    text,
    mgray,
    success,
    warning,
    danger,
  },
  primaryColor: 'brand',
  primaryShade: { light: 7, dark: 5 },
  fontFamily: 'Inter, sans-serif',
  defaultRadius: 0,

  headings: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: '400',
    sizes: {
      h1: { fontSize: rem(30), lineHeight: rem(38), fontWeight: '600' },
      h2: { fontSize: rem(20), lineHeight: rem(26), fontWeight: '600' },
      h3: { fontSize: rem(18), lineHeight: rem(24), fontWeight: '500' },
      h4: { fontSize: rem(16), lineHeight: rem(22), fontWeight: '500' },
      h5: { fontSize: rem(14), lineHeight: rem(20), fontWeight: '500' },
      // h6 omitted — below 14 px type floor per style guide
    },
  },

  // 300 px task-pane constraint: 14 px body default instead of guide's 16 px minimum
  fontSizes: {
    xs: rem(10),
    sm: rem(12),
    md: rem(14),
    lg: rem(16),
    xl: rem(18),
  },

  breakpoints: {
    xs: '30em', // 480 px
  },

  spacing: {
    xxs: rem(4),
  },

  radius: {
    xs: rem(4),
    sm: rem(6),
    md: rem(8),
    lg: rem(12),
    xl: rem(16),
    // pill: rem(10) — add here when chips/badges are introduced
  },

  black: 'var(--mantine-color-black)',

  other: {
    fontWeightSemiBold: 600,
    '--mantine-font-weight-semibold': '600',

    '--claim-sidebar-bg': 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-7))',
    '--claim-sidebar-height': 'calc(100vh - rem(80))',

    // ClaimTabs design tokens
    '--claimTabs-gap': '4px',
    '--claimTabs-minHeight': '60px',
    '--claimTabs-inactiveBg': 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-6))',
    '--claimTabs-inactiveText': 'light-dark(var(--mantine-color-text-4), var(--mantine-color-gray-4))',
    '--claimTabs-inactiveBorder': 'transparent',
    '--claimTabs-activeBg': 'light-dark(var(--mantine-color-brand-2), var(--mantine-color-brand-8))',
    '--claimTabs-activeText': 'light-dark(var(--mantine-color-brand-7), var(--mantine-color-brand-4))',
    '--claimTabs-activeBorder': 'transparent',
    '--claimTabs-labelFontSize': '10px',
    '--claimTabs-labelLineHeight': '1.1',
    '--claimTabs-labelFontWeight': '400',

    // Shared color tokens for light/dark mode
    '--input-bg': 'light-dark(var(--mantine-color-brand-3), var(--mantine-color-dark-6))', // #DBEBF2 / dark
    '--section-bg': 'light-dark(var(--mantine-color-mgray-5), var(--mantine-color-dark-7))', // #F5F5F5 / dark
    '--attachment-panel-bg': 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-7))',
  },

  components: {
    Button: {
      defaultProps: {
        fw: 400,
      },
      // Every button is 40px tall whatever its size prop. Filled primary and success buttons
      // also take their colours from cssVariablesResolver, so dark mode can use the design's
      // brighter fills with dark text.
      vars: (_theme: MantineTheme, props: ButtonProps) => {
        const height = { '--button-height': rem(40) }
        const token = getFilledButtonToken(props)
        if (!token) return { root: height }
        return {
          root: {
            ...height,
            '--button-bg': `var(--button-${token}-bg)`,
            '--button-hover': `var(--button-${token}-hover)`,
            '--button-color': `var(--button-${token}-color)`,
            '--button-hover-color': `var(--button-${token}-color)`,
          },
        }
      },
    },

    Select: {
      defaultProps: {
        fw: 400,
      },
      styles: {
        input: {
          backgroundColor: 'var(--input-bg)',
        },
      },
    },

    TextInput: {
      styles: {
        input: {
          backgroundColor: 'var(--input-bg)',
        },
      },
    },

    NumberInput: {
      styles: {
        input: {
          backgroundColor: 'var(--input-bg)',
        },
      },
    },

    Textarea: {
      styles: {
        input: {
          backgroundColor: 'var(--input-bg)',
        },
      },
    },

    Checkbox: {
      styles: {
        input: {
          cursor: 'pointer',
        },
      },
    },

    ActionIcon: {
      styles: {
        root: {
          backgroundColor: 'var(--input-bg)',
          color: 'light-dark(var(--mantine-color-brand-8), var(--mantine-color-brand-4))',
        },
      },
    },

    Card: {
      defaultProps: {
        shadow: 'sm',
        padding: 'md',
        withBorder: true,
      },
    },

    Paper: {
      defaultProps: {
        radius: 0,
      },
    },

    Anchor: {
      defaultProps: {
        c: 'var(--link-color)', // Main Blue (#003591); lighter in dark mode — see cssVariablesResolver
        underline: 'always', // per style guide link spec
      },
    },

    Icon: {
      defaultProps: {
        size: 16,
      },
    },

    // Mantine's dark tooltip text is var(--mantine-color-black), which theme.black makes
    // self-referencing — it resolves to nothing and inherits white text on the light gray
    // background. Both colours are pinned so tooltips stay legible in either mode.
    Tooltip: {
      styles: {
        tooltip: {
          backgroundColor: 'light-dark(var(--mantine-color-dark-6), var(--mantine-color-gray-2))',
          color: 'light-dark(var(--mantine-color-white), var(--mantine-color-dark-7))',
        },
      },
    },
  },
})

// Mantine's stock error red (#fa5252 / #c92a2a) is not the guide's red, and
// --mantine-color-error drives every input `error=` message, error border and
// `withAsterisk` asterisk — remapping it here fixes all of those in one place.
// danger.6 / success.6 are the guide's mains, but both fail contrast on the Outlook
// dark background (2.5:1 and 3.3:1), so each is pinned lighter there. Links likewise:
// Main Blue is near-invisible on dark, so they switch to the brand's bright blue. The
// "Unsaved" cream would glare on dark and wash out the light text on it, so dark mode
// gets a faint amber tint with a brighter amber label instead.
// Filled primary/success buttons (see Button.vars) follow the UI/UX mockup: Main Blue and
// guide green with white text in light mode; in dark mode the brighter #0095DA / #65E05D
// with #242424 text, because white on either fails contrast (3.3:1 and 1.7:1).
// Body text is set explicitly: theme.black is self-referencing, so light text otherwise
// falls back to pure black. The header claim link is #333333 in light mode per the mockup.
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {},
  light: {
    '--mantine-color-text': 'var(--mantine-color-text-0)',
    '--mantine-color-error': 'var(--mantine-color-danger-6)',
    '--status-success-color': 'var(--mantine-color-success-6)',
    '--link-color': 'var(--mantine-color-brand-7)',
    '--header-link-color': 'var(--mantine-color-text-0)',
    '--unsaved-bg': 'var(--mantine-color-warning-0)',
    '--unsaved-text': 'var(--mantine-color-warning-6)',
    '--button-primary-bg': 'var(--mantine-color-brand-7)',
    '--button-primary-hover': 'var(--mantine-color-brand-8)',
    '--button-primary-color': 'var(--mantine-color-white)',
    '--button-success-bg': 'var(--mantine-color-success-6)',
    '--button-success-hover': 'var(--mantine-color-success-7)',
    '--button-success-color': 'var(--mantine-color-white)',
  },
  dark: {
    '--mantine-color-text': 'var(--mantine-color-white)',
    '--mantine-color-error': 'var(--mantine-color-danger-3)',
    '--status-success-color': 'var(--mantine-color-success-4)',
    '--link-color': '#0095DA',
    '--header-link-color': 'var(--link-color)',
    '--unsaved-bg': 'rgba(255, 179, 0, 0.12)',
    '--unsaved-text': 'var(--mantine-color-warning-2)',
    '--button-primary-bg': '#0095DA',
    '--button-primary-hover': 'color-mix(in srgb, #0095DA 85%, black)',
    '--button-primary-color': 'var(--mantine-color-dark-7)',
    '--button-success-bg': '#65E05D',
    '--button-success-hover': 'color-mix(in srgb, #65E05D 85%, black)',
    '--button-success-color': 'var(--mantine-color-dark-7)',
  },
})
